import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Header from '../components/layout/Header';
import ConfirmModal from '../components/common/ConfirmModal';
import { useAuth } from '../context/AuthContext';
import {
  getEvents, createEvent, updateEvent, deleteEvent,
  getCases, getUsers, getCustomLists, getCase,
} from '../utils/api';
import {
  Plus, ChevronLeft, ChevronRight, X, Check,
  Stethoscope, Scale, AlertCircle, Users,
  CalendarDays, Clock, Link2, User, FileText,
  Search, CheckCircle,
} from 'lucide-react';
import toast from 'react-hot-toast';

// ── Constants ────────────────────────────────────────────────────────────────
const TYPE_CONFIG = {
  treatment: { label: 'Treatment Appt',     color: '#0a7069', bg: 'rgba(10,112,105,0.18)',  text: '#0d9488', icon: Stethoscope, db: 'treatment'  },
  hearing:   { label: 'Hearing/Deposition', color: '#3b82f6', bg: 'rgba(59,130,246,0.18)',  text: '#93c5fd', icon: Scale,       db: 'hearing'    },
  deadline:  { label: 'Deadline',           color: '#ef4444', bg: 'rgba(239,68,68,0.18)',   text: '#fca5a5', icon: AlertCircle, db: 'deadline'   },
  mediation: { label: 'Mediation',          color: '#f59e0b', bg: 'rgba(245,158,11,0.18)',  text: '#fcd34d', icon: Users,   db: 'mediation'  },
  sol:       { label: 'SOL / Key Date',     color: '#a855f7', bg: 'rgba(168,85,247,0.18)',  text: '#d8b4fe', icon: CalendarDays,db: 'sol'        },
};

const EMPTY_FORM = {
  event_type: 'treatment', title: '', date: '', time: '09:00',
  case_id: '', provider_name: '', appointment_type: '',
  assigned_to: '', notes: '', location: '',
};

function pad(n) { return String(n).padStart(2, '0'); }
function toDateStr(y, m, d) { return `${y}-${pad(m + 1)}-${pad(d)}`; }

function fmtMonthYear(y, m) {
  return new Date(y, m, 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}

function fmtShortDate(dateStr) {
  if (!dateStr) return '—';
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

function fmtTime(t) {
  if (!t) return '';
  const [h, m] = t.split(':').map(Number);
  const ap = h >= 12 ? 'PM' : 'AM';
  return `${h % 12 || 12}:${pad(m)} ${ap}`;
}

function eventDateStr(ev) {
  if (!ev.start_time) return '';
  return ev.start_time.split('T')[0];
}

function eventTimeStr(ev) {
  if (!ev.start_time) return '';
  const t = ev.start_time.split('T')[1];
  return t ? t.slice(0, 5) : '';
}

// ── Sub-components ───────────────────────────────────────────────────────────
function TypeChip({ type, selected, onClick }) {
  const cfg = TYPE_CONFIG[type];
  const Icon = cfg.icon;
  return (
    <button
      onClick={() => onClick(type)}
      style={{
        display: 'flex', alignItems: 'center', gap: 7,
        padding: '8px 12px', borderRadius: 8, cursor: 'pointer',
        border: `2px solid ${selected ? cfg.color : 'var(--zeus-border)'}`,
        background: selected ? cfg.bg : 'transparent',
        color: selected ? cfg.text : 'var(--text-muted)',
        fontWeight: selected ? 700 : 400, fontSize: '0.82rem',
        transition: 'all 0.12s', fontFamily: 'inherit', textAlign: 'left',
      }}
    >
      <Icon size={13} style={{ flexShrink: 0 }} />
      {cfg.label}
    </button>
  );
}

function Toggle({ checked, onChange }) {
  return (
    <div
      onClick={() => onChange(!checked)}
      style={{
        width: 38, height: 22, borderRadius: 11, position: 'relative',
        cursor: 'pointer', flexShrink: 0, transition: 'background 0.2s',
        background: checked ? '#22c55e' : 'var(--zeus-border)',
      }}
    >
      <div style={{
        position: 'absolute', top: 2, borderRadius: '50%',
        width: 18, height: 18, background: '#fff',
        boxShadow: '0 1px 4px rgba(0,0,0,0.25)',
        transition: 'left 0.18s', left: checked ? 18 : 2,
      }} />
    </div>
  );
}

function EventPill({ ev, onClick }) {
  const cfg = TYPE_CONFIG[ev.event_type] || TYPE_CONFIG.deadline;
  const t = eventTimeStr(ev);
  return (
    <div
      onClick={(e) => { e.stopPropagation(); onClick(ev); }}
      style={{
        padding: '2px 6px', borderRadius: 4,
        background: cfg.bg, borderLeft: `2px solid ${cfg.color}`,
        color: cfg.text, fontSize: '0.67rem', fontWeight: 600,
        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        cursor: 'pointer', flexShrink: 0,
      }}
    >
      {t ? `${fmtTime(t).replace(' AM', 'a').replace(' PM', 'p')} ` : ''}{ev.title}
    </div>
  );
}

// ── Main Component ───────────────────────────────────────────────────────────
export default function CalendarPage() {
  const { user } = useAuth();
  const location = useLocation();
  const navigate  = useNavigate();
  const today = useMemo(() => new Date(), []);
  const todayStr = useMemo(() => toDateStr(today.getFullYear(), today.getMonth(), today.getDate()), [today]);

  // Calendar nav
  const [curYear,  setCurYear]  = useState(today.getFullYear());
  const [curMonth, setCurMonth] = useState(today.getMonth());

  // Data
  const [events,       setEvents]       = useState([]);
  const [cases,        setCases]        = useState([]);
  const [users,        setUsers]        = useState([]);
  const [treatTypes,   setTreatTypes]   = useState([]);
  const [loading,      setLoading]      = useState(true);

  // Treatment flow
  const [caseProviders,    setCaseProviders]    = useState([]);  // treating providers for selected case
  const [loadingProviders, setLoadingProviders] = useState(false);
  const [providerSearch,   setProviderSearch]   = useState('');
  const [providerOpen,     setProviderOpen]     = useState(false);
  const providerRef      = useRef(null);
  const providerInputRef = useRef(null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, width: 0 });

  // Case search combobox
  const [caseSearch,    setCaseSearch]    = useState('');
  const [caseOpen,      setCaseOpen]      = useState(false);
  const [caseLabel,     setCaseLabel]     = useState('');
  const [caseDropPos,   setCaseDropPos]   = useState({ top: 0, left: 0, width: 0 });
  const caseInputRef = useRef(null);
  const caseRef      = useRef(null);

  // Modal state
  const [confirmDelete, setConfirmDelete] = useState(null); // event to delete
  const [showAdd,    setShowAdd]    = useState(false);
  const [showDetail, setShowDetail] = useState(false);
  const [detailEv,   setDetailEv]   = useState(null);
  const [form,       setForm]       = useState({ ...EMPTY_FORM });
  const [saving,     setSaving]     = useState(false);
  const [initDate,   setInitDate]   = useState('');

  // ── Load data ──────────────────────────────────────────────
  const loadEvents = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getEvents();
      const eventsArr = Array.isArray(data) ? data : (data?.events || []);
      // Enrich with cases from local state
      setCases(prevCases => {
        const caseMap = {};
        prevCases.forEach(c => { caseMap[c._id || c.id] = c; });
        setEvents(eventsArr.map(ev => {
          if (!ev.case_id || ev.case?.client_name) return ev;
          const c = caseMap[ev.case_id];
          if (!c) return ev;
          return { ...ev, case: { id: c._id||c.id, case_number: c.case_number, client_name: c.client_name, date_of_birth: c.date_of_birth } };
        }));
        return prevCases;
      });
    } catch (err) {
      console.error('[CalendarPage] loadEvents failed:', err?.message || err);
    } finally { setLoading(false); }
  }, []);

  // Load events once on mount + after creates/deletes
  useEffect(() => { loadEvents(); }, []);

  // If navigated from Dashboard with a specific event ID, open its detail modal
  useEffect(() => {
    const openId = location?.state?.openEventId;
    if (!openId || events.length === 0) return;
    const ev = events.find(e => (e.id || e._id) === openId);
    if (ev) {
      // Jump calendar to the event's month
      const ds = eventDateStr(ev);
      if (ds) {
        const [y, m] = ds.split('-').map(Number);
        setCurYear(y);
        setCurMonth(m - 1);
      }
      openDetail(ev);
      // Clear the state so re-renders don't reopen it
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [events, location?.state?.openEventId]);

  useEffect(() => {
    async function loadSupport() {
      try {
        const [cRes, uRes, lRes] = await Promise.all([
          getCases({ limit: 300 }),
          getUsers(),
          getCustomLists(),
        ]);
        const casesArr = Array.isArray(cRes) ? cRes : (cRes?.cases || []);
        setCases(casesArr);
        setUsers(Array.isArray(uRes) ? uRes : (uRes?.users || []));
        setTreatTypes(lRes?.treatment_type_options || [
          'Initial Consultation', 'Follow-Up', 'MRI / Imaging', 'X-Ray',
          'Physical Therapy Session', 'Chiropractic Adjustment', 'Pain Management',
          'Epidural Injection', 'Surgery Pre-Op', 'Post-Op Follow-Up',
          'Specialist Referral', 'Lab Work',
        ]);
        // Now enrich events with the freshly-loaded cases
        setEvents(prev => {
          const caseMap = {};
          casesArr.forEach(c => { caseMap[c._id || c.id] = c; });
          return prev.map(ev => {
            if (!ev.case_id || ev.case?.client_name) return ev;
            const c = caseMap[ev.case_id];
            if (!c) return ev;
            return { ...ev, case: { id: c._id || c.id, case_number: c.case_number, client_name: c.client_name, date_of_birth: c.date_of_birth } };
          });
        });
      } catch (err) { console.error(err); }
    }
    loadSupport();
  }, []);

  // Click-outside for provider dropdown
  useEffect(() => {
    const handler = (e) => {
      if (providerRef.current && !providerRef.current.contains(e.target)) {
        setProviderOpen(false);
      }
      if (caseRef.current && !caseRef.current.contains(e.target)) {
        setCaseOpen(false);
        setCaseSearch('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // ── Load case's treating providers from consultations ──────
  const loadCaseProviders = useCallback(async (caseId) => {
    if (!caseId) { setCaseProviders([]); return; }
    setLoadingProviders(true);
    try {
      const caseData = await getCase(caseId);
      const consults = caseData?.consultations || caseData?.case?.consultations || [];
      // Extract unique provider names
      const seen = new Set();
      const providers = [];
      consults.forEach(c => {
        if (c.provider_name && !seen.has(c.provider_name)) {
          seen.add(c.provider_name);
          providers.push({ name: c.provider_name, type: c.provider_type || '' });
        }
      });
      setCaseProviders(providers);
    } catch (err) {
      console.error(err);
      setCaseProviders([]);
    } finally { setLoadingProviders(false); }
  }, []);

  // ── Navigation ─────────────────────────────────────────────
  const prev = () => {
    if (curMonth === 0) { setCurYear(y => y - 1); setCurMonth(11); }
    else setCurMonth(m => m - 1);
  };
  const next = () => {
    if (curMonth === 11) { setCurYear(y => y + 1); setCurMonth(0); }
    else setCurMonth(m => m + 1);
  };
  const goToday = () => { setCurYear(today.getFullYear()); setCurMonth(today.getMonth()); };

  // ── Calendar grid ──────────────────────────────────────────
  const calCells = useMemo(() => {
    const firstDay = new Date(curYear, curMonth, 1).getDay();
    const daysInMonth = new Date(curYear, curMonth + 1, 0).getDate();
    const daysInPrev  = new Date(curYear, curMonth, 0).getDate();
    const cells = [];
    for (let i = 0; i < 42; i++) {
      let d, m = curMonth, y = curYear, other = false;
      if (i < firstDay) {
        d = daysInPrev - firstDay + i + 1; m = curMonth - 1; other = true;
        if (m < 0) { m = 11; y = curYear - 1; }
      } else if (i >= firstDay + daysInMonth) {
        d = i - firstDay - daysInMonth + 1; m = curMonth + 1; other = true;
        if (m > 11) { m = 0; y = curYear + 1; }
      } else {
        d = i - firstDay + 1;
      }
      cells.push({ dateStr: toDateStr(y, m, d), day: d, other, weekend: i % 7 === 0 || i % 7 === 6 });
    }
    return cells;
  }, [curYear, curMonth]);

  const eventsByDate = useMemo(() => {
    const map = {};
    events.forEach(ev => {
      const ds = eventDateStr(ev);
      if (!map[ds]) map[ds] = [];
      map[ds].push(ev);
    });
    return map;
  }, [events]);

  const upcomingEvents = useMemo(() => {
    return events
      .filter(ev => eventDateStr(ev) >= todayStr)
      .sort((a, b) => a.start_time.localeCompare(b.start_time))
      .slice(0, 10);
  }, [events, todayStr]);

  // ── Open add modal ─────────────────────────────────────────
  const openAdd = useCallback((dateStr) => {
    setForm({ ...EMPTY_FORM, date: dateStr || todayStr });
    setInitDate(dateStr || '');
    setCaseProviders([]);
    setProviderSearch('');
    setProviderOpen(false);
    setCaseSearch('');
    setCaseOpen(false);
    setCaseLabel('');
    setShowAdd(true);
  }, [todayStr]);

  // ── Form handlers ──────────────────────────────────────────
  const setF = (key, val) => setForm(p => ({ ...p, [key]: val }));

  const onTypeChange = (type) => {
    setF('event_type', type);
    if (type !== 'treatment') {
      setF('provider_name', '');
      setF('appointment_type', '');
    }
  };

  const onCaseChange = (caseId) => {
    setF('case_id', caseId);
    setF('provider_name', '');
    setF('appointment_type', '');
    setProviderSearch('');
    setCaseProviders([]);
    if (caseId && form.event_type === 'treatment') {
      loadCaseProviders(caseId);
    }
  };

  const openCaseDropdown = () => {
    if (caseInputRef.current) {
      const rect = caseInputRef.current.getBoundingClientRect();
      setCaseDropPos({ top: rect.bottom + 4, left: rect.left, width: rect.width });
    }
    setCaseOpen(true);
  };

  const selectCase = (c) => {
    const id  = c._id || c.id;
    const lbl = `${c.case_number || id.slice(0,8)}${c.client_name ? ' — ' + c.client_name : ''}`;
    setCaseLabel(lbl);
    setCaseSearch('');
    setCaseOpen(false);
    onCaseChange(id);
  };

  const clearCase = () => {
    setCaseLabel('');
    setCaseSearch('');
    setCaseOpen(false);
    onCaseChange('');
  };

  const openProviderDropdown = () => {
    if (providerInputRef.current) {
      const rect = providerInputRef.current.getBoundingClientRect();
      setDropdownPos({ top: rect.bottom + 4, left: rect.left, width: rect.width });
    }
    setProviderOpen(true);
  };

  const selectProvider = (p) => {
    setF('provider_name', p.name);
    setProviderSearch('');
    setProviderOpen(false);
    // Auto-fill title if empty
    if (!form.title) {
      setForm(prev => ({ ...prev, provider_name: p.name, title: p.name }));
    }
  };

  const filteredCases = useMemo(() => {
    const q = caseSearch.trim().toLowerCase();
    if (!q) return cases;
    return cases.filter(c => {
      const num  = (c.case_number || '').toLowerCase();
      const name = (c.client_name || '').toLowerCase();
      const doi  = (c.date_of_incident || '').toLowerCase();
      const claim= (c.claim_number || '').toLowerCase();
      const dob  = (c.date_of_birth || '').toLowerCase();
      return num.includes(q) || name.includes(q) || doi.includes(q) || claim.includes(q) || dob.includes(q);
    });
  }, [cases, caseSearch]);

  const filteredProviders = useMemo(() => {
    const q = providerSearch.toLowerCase();
    return caseProviders.filter(p =>
      !q || p.name.toLowerCase().includes(q) || p.type.toLowerCase().includes(q)
    );
  }, [caseProviders, providerSearch]);

  // ── Save event ─────────────────────────────────────────────
  const handleSave = async () => {
    if (!form.title.trim()) { toast.error('Event title is required'); return; }
    if (!form.date)          { toast.error('Date is required'); return; }
    if (form.event_type === 'treatment' && !form.case_id) {
      toast.error('A case is required for Treatment Appointments'); return;
    }

    setSaving(true);
    try {
      const startTime = form.time
        ? `${form.date}T${form.time}:00`
        : `${form.date}T00:00:00`;

      const payload = {
        title:            form.title.trim(),
        event_type:       form.event_type,
        start_time:       startTime,
        case_id:          form.case_id || null,
        provider_name:    form.provider_name || '',
        appointment_type: form.appointment_type || '',
        assigned_to:      form.assigned_to || null,
        description:      form.notes || '',
        location:         form.location || '',
        all_day:          !form.time,
      };

      const created = await createEvent(payload);
      // Optimistically add the newly created event (server now includes case.client_name)
      if (created && (created.id || created._id)) {
        setEvents(prev => [...prev, { ...created, _id: created.id || created._id }]);
      }
      // Then reload to ensure full consistency with server state
      loadEvents();
      if (form.event_type === 'treatment') {
        const providerPart = form.provider_name ? ` with ${form.provider_name}` : '';
        const typePart     = form.appointment_type ? ` (${form.appointment_type})` : '';
        toast.success(
          `Consultation${providerPart}${typePart} added to the Treatment tab`,
          { duration: 5000 }
        );
      } else {
        toast.success('Event created');
      }
      setShowAdd(false);
      loadEvents();
    } catch (err) {
      toast.error(err?.message || 'Failed to create event');
    } finally { setSaving(false); }
  };

  // ── Delete event ───────────────────────────────────────────
  const handleDelete = (ev) => {
    setConfirmDelete(ev);
  };

  const doDelete = async () => {
    if (!confirmDelete) return;
    try {
      await deleteEvent(confirmDelete.id || confirmDelete._id);
      toast.success('Event deleted');
      setConfirmDelete(null);
      setShowDetail(false);
      setEvents(prev => prev.filter(e => (e.id || e._id) !== (confirmDelete.id || confirmDelete._id)));
    } catch { toast.error('Failed to delete'); }
  };

  const openDetail = (ev) => { setDetailEv(ev); setShowDetail(true); };

  // ── Mini calendar ──────────────────────────────────────────
  const miniCells = useMemo(() => {
    const firstDay = new Date(curYear, curMonth, 1).getDay();
    const daysInMonth = new Date(curYear, curMonth + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < firstDay; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(d);
    return cells;
  }, [curYear, curMonth]);

  // ── Render ─────────────────────────────────────────────────
  return (
    <>
      <Header title="Calendar">
        <button className="btn btn-primary" onClick={() => openAdd('')}>
          <Plus size={15} /> Add Event
        </button>
      </Header>

      <div style={{ display: 'flex', height: 'calc(100vh - 60px)', overflow: 'hidden' }}>

        {/* ── Calendar area ── */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', padding: '12px 12px 12px 16px' }}>

          {/* Nav bar */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, flexShrink: 0 }}>
            <button className="btn btn-ghost btn-sm" onClick={prev}><ChevronLeft size={16} /></button>
            <button className="btn btn-ghost btn-sm" onClick={goToday} style={{ fontSize: '0.78rem', fontWeight: 600 }}>Today</button>
            <button className="btn btn-ghost btn-sm" onClick={next}><ChevronRight size={16} /></button>
            <span style={{ fontWeight: 700, fontSize: '1.05rem', color: 'var(--text-primary)', marginLeft: 4 }}>
              {fmtMonthYear(curYear, curMonth)}
            </span>
            {loading && <div className="spinner" style={{ width: 14, height: 14, marginLeft: 8 }} />}
          </div>

          {/* Day headers */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', marginBottom: 3, flexShrink: 0 }}>
            {['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].map((d, i) => (
              <div key={d} style={{
                textAlign: 'center', fontSize: '0.7rem', fontWeight: 700,
                letterSpacing: '0.07em', textTransform: 'uppercase',
                color: i === 0 || i === 6 ? 'var(--zeus-gold)' : 'var(--text-muted)',
                padding: '4px 0',
              }}>{d}</div>
            ))}
          </div>

          {/* Grid */}
          <div style={{ flex: 1, display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gridTemplateRows: 'repeat(6,1fr)', gap: 3, overflow: 'hidden' }}>
            {calCells.map((cell, i) => {
              const dayEvs = eventsByDate[cell.dateStr] || [];
              const isToday = cell.dateStr === todayStr;
              const maxShow = 3;
              return (
                <div
                  key={i}
                  onClick={() => openAdd(cell.dateStr)}
                  style={{
                    background: 'var(--zeus-navy-light)',
                    border: `1px solid ${isToday ? 'var(--zeus-gold)' : 'var(--zeus-border)'}`,
                    borderRadius: 6,
                    padding: '5px 5px 4px',
                    cursor: 'pointer',
                    opacity: cell.other ? 0.35 : 1,
                    display: 'flex', flexDirection: 'column', gap: 2,
                    overflow: 'hidden', minHeight: 0,
                    transition: 'border-color 0.12s, background 0.12s',
                  }}
                  onMouseEnter={e => { if (!isToday) e.currentTarget.style.borderColor = 'var(--zeus-border)'; e.currentTarget.style.background = 'var(--zeus-navy)'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = isToday ? 'var(--zeus-gold)' : 'var(--zeus-border)'; e.currentTarget.style.background = 'var(--zeus-navy-light)'; }}
                >
                  <div style={{
                    fontSize: '0.72rem', fontWeight: 700, flexShrink: 0,
                    color: cell.weekend ? 'var(--zeus-gold)' : 'var(--text-primary)',
                    ...(isToday ? {
                      background: 'var(--zeus-gold)', color: '#0f1f38',
                      width: 20, height: 20, borderRadius: '50%',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: '0.68rem',
                    } : {}),
                  }}>{cell.day}</div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: 2, flex: 1, overflow: 'hidden' }}>
                    {dayEvs.slice(0, maxShow).map(ev => (
                      <EventPill key={ev.id || ev._id} ev={ev} onClick={openDetail} />
                    ))}
                    {dayEvs.length > maxShow && (
                      <div
                        style={{ fontSize: '0.62rem', color: 'var(--text-muted)', paddingLeft: 2, cursor: 'pointer' }}
                        onClick={(e) => { e.stopPropagation(); openDetail(dayEvs[maxShow]); }}
                      >
                        +{dayEvs.length - maxShow} more
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* ── Right sidebar ── */}
        <div style={{
          width: 248, flexShrink: 0, borderLeft: '1px solid var(--zeus-border)',
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
          background: 'var(--zeus-navy)',
        }}>
          {/* Legend */}
          <div style={{ padding: '14px 16px', borderBottom: '1px solid var(--zeus-border)' }}>
            <div style={{ fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 8 }}>Event Types</div>
            {Object.entries(TYPE_CONFIG).map(([k, cfg]) => {
              const Icon = cfg.icon;
              return (
                <div key={k} style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 5, fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                  <div style={{ width: 10, height: 10, borderRadius: 3, background: cfg.color, flexShrink: 0 }} />
                  {cfg.label}
                </div>
              );
            })}
          </div>

          {/* Mini calendar */}
          <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--zeus-border)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
              <button onClick={prev} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: '2px 4px', borderRadius: 4, fontSize: 12 }}>‹</button>
              <div style={{ fontSize: '0.8rem', fontWeight: 700, color: 'var(--text-primary)' }}>
                {new Date(curYear, curMonth, 1).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
              </div>
              <button onClick={next} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: '2px 4px', borderRadius: 4, fontSize: 12 }}>›</button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: 1 }}>
              {['S','M','T','W','T','F','S'].map((d, i) => (
                <div key={i} style={{ textAlign: 'center', fontSize: '0.6rem', fontWeight: 700, color: 'var(--text-muted)', padding: '2px 0' }}>{d}</div>
              ))}
              {miniCells.map((d, i) => {
                if (!d) return <div key={i} />;
                const ds = toDateStr(curYear, curMonth, d);
                const isT = ds === todayStr;
                const hasEv = !!(eventsByDate[ds]?.length);
                return (
                  <div
                    key={i}
                    onClick={() => openAdd(ds)}
                    style={{
                      textAlign: 'center', fontSize: '0.7rem', padding: '3px 0',
                      borderRadius: '50%', cursor: 'pointer',
                      background: isT ? 'var(--zeus-gold)' : 'transparent',
                      color: isT ? '#0f1f38' : hasEv ? 'var(--text-primary)' : 'var(--text-muted)',
                      fontWeight: isT || hasEv ? 700 : 400,
                      transition: 'background 0.1s',
                    }}
                    onMouseEnter={e => { if (!isT) e.currentTarget.style.background = 'var(--zeus-navy-light)'; }}
                    onMouseLeave={e => { if (!isT) e.currentTarget.style.background = 'transparent'; }}
                  >
                    {d}
                    {hasEv && !isT && <div style={{ width: 4, height: 4, borderRadius: '50%', background: 'var(--zeus-gold)', margin: '0 auto', marginTop: -2 }} />}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Upcoming events */}
          <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            <div style={{ fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'var(--text-muted)', padding: '10px 16px 4px' }}>Upcoming</div>
            <div style={{ flex: 1, overflowY: 'auto' }}>
              {upcomingEvents.length === 0 ? (
                <div style={{ padding: '16px', textAlign: 'center', fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                  No upcoming events
                </div>
              ) : upcomingEvents.map(ev => {
                const cfg = TYPE_CONFIG[ev.event_type] || TYPE_CONFIG.deadline;
                return (
                  <div
                    key={ev.id || ev._id}
                    onClick={() => openDetail(ev)}
                    style={{
                      display: 'flex', alignItems: 'flex-start', gap: 8,
                      padding: '7px 16px', cursor: 'pointer', transition: 'background 0.1s',
                      borderBottom: '1px solid rgba(255,255,255,0.04)',
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.04)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    <div style={{ width: 8, height: 8, borderRadius: '50%', background: cfg.color, flexShrink: 0, marginTop: 4 }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '0.82rem', fontWeight: 600, color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {ev.title}
                      </div>
                      <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: 1 }}>
                        {fmtShortDate(eventDateStr(ev))}{eventTimeStr(ev) ? ` · ${fmtTime(eventTimeStr(ev))}` : ''}
                      </div>
                      {ev.case?.client_name && (
                        <div style={{ fontSize: '0.68rem', color: 'var(--zeus-gold)', marginTop: 1, fontWeight: 600 }}>
                          {ev.case.client_name}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* ══ ADD EVENT MODAL ══════════════════════════════════════ */}
      {showAdd && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.55)' }} onClick={() => setShowAdd(false)} />
          <div style={{
            position: 'relative', width: 500, maxWidth: '96vw', maxHeight: '92vh',
            background: 'var(--zeus-navy)', border: '1px solid var(--zeus-border)',
            borderRadius: 12, boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
            display: 'flex', flexDirection: 'column', overflow: 'hidden',
          }}>
            {/* Header */}
            <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--zeus-border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'var(--zeus-navy-light)', flexShrink: 0 }}>
              <span style={{ fontWeight: 700, fontSize: '1rem', color: 'var(--text-primary)' }}>
                {initDate ? `New Event — ${fmtShortDate(initDate)}` : 'New Event'}
              </span>
              <button onClick={() => setShowAdd(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', display: 'flex', padding: 4 }}>
                <X size={16} />
              </button>
            </div>

            {/* Body */}
            <div style={{ padding: '18px 20px', overflowY: 'auto', flex: 1, display: 'flex', flexDirection: 'column', gap: 14 }}>

              {/* Event type */}
              <div className="form-group">
                <label className="form-label">Event Type *</label>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 7 }}>
                  {Object.keys(TYPE_CONFIG).map(t => (
                    <TypeChip key={t} type={t} selected={form.event_type === t} onClick={onTypeChange} />
                  ))}
                </div>
              </div>

              {/* Treatment: case first banner */}
              {form.event_type === 'treatment' && (
                <div style={{
                  display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 14px',
                  borderRadius: 8, border: '1px solid rgba(10,112,105,0.5)', background: 'rgba(10,112,105,0.12)',
                  fontSize: '0.82rem', color: '#0d9488',
                }}>
                  <Stethoscope size={15} style={{ flexShrink: 0, marginTop: 1 }} />
                  <div>
                    <span style={{ display: 'block', fontSize: '0.85rem', color: 'inherit', fontWeight: 600, marginBottom: 2 }}>
                      Select a case first
                    </span>
                    Provider list comes from the case's Treatment tab. Saving will auto-create a consultation in the Treatment Tab.
                  </div>
                </div>
              )}

              {/* Case — searchable combobox */}
              <div
                ref={caseRef}
                className="form-group"
                style={{
                  position: 'relative',
                  ...(form.event_type === 'treatment' ? {
                    padding: 10, borderRadius: 8, marginBottom: -4,
                    border: `1px solid ${form.case_id ? 'rgba(13,148,136,0.5)' : 'rgba(239,68,68,0.3)'}`,
                    background: form.case_id ? 'rgba(13,148,136,0.06)' : 'rgba(239,68,68,0.05)',
                  } : {}),
                }}
              >
                <label className="form-label">
                  Linked Case{form.event_type === 'treatment' && <span style={{ color: '#ef4444', marginLeft: 3 }}>*</span>}
                </label>

                {/* Selected case chip */}
                {form.case_id && !caseOpen ? (
                  <div style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '7px 10px', borderRadius: 7,
                    border: '1px solid rgba(13,148,136,0.4)',
                    background: 'rgba(13,148,136,0.08)',
                    fontSize: '0.84rem', color: 'var(--text-primary)', fontWeight: 600,
                  }}>
                    <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {caseLabel}
                    </span>
                    <div style={{ display: 'flex', gap: 6, flexShrink: 0, marginLeft: 8 }}>
                      <button
                        type="button"
                        onClick={() => { setCaseOpen(true); setTimeout(() => caseInputRef.current?.focus(), 30); }}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--zeus-gold)', fontSize: '0.72rem', fontWeight: 700, padding: '0 4px', fontFamily: 'inherit' }}
                      >
                        Change
                      </button>
                      <button type="button" onClick={clearCase}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', padding: 2 }}>
                        <X size={13} />
                      </button>
                    </div>
                  </div>
                ) : (
                  /* Search input */
                  <div ref={caseInputRef} style={{
                    display: 'flex', alignItems: 'center', gap: 7,
                    padding: '7px 10px', borderRadius: 7,
                    border: `1px solid ${caseOpen ? 'var(--zeus-gold)' : 'var(--zeus-border)'}`,
                    background: 'var(--zeus-navy-light)', transition: 'border-color 0.15s',
                  }}>
                    <Search size={12} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
                    <input
                      style={{ background: 'none', border: 'none', outline: 'none', flex: 1, fontSize: '0.84rem', color: 'var(--text-primary)', fontFamily: 'inherit' }}
                      placeholder="Search by client name, case #, claim #, DOI..."
                      value={caseSearch}
                      onChange={e => { setCaseSearch(e.target.value); openCaseDropdown(); }}
                      onFocus={openCaseDropdown}
                      autoComplete="off"
                    />
                    {caseSearch && (
                      <button type="button" onClick={() => { setCaseSearch(''); setCaseOpen(false); }}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', padding: 0 }}>
                        <X size={12} />
                      </button>
                    )}
                  </div>
                )}

                {/* Fixed-position dropdown */}
                {caseOpen && (
                  <div style={{
                    position: 'fixed',
                    top: caseDropPos.top,
                    left: caseDropPos.left,
                    width: caseDropPos.width,
                    zIndex: 99999,
                    background: 'var(--zeus-navy)', border: '1px solid var(--zeus-border)',
                    borderRadius: 8, boxShadow: '0 8px 32px rgba(0,0,0,0.7)',
                    maxHeight: 220, overflowY: 'auto',
                  }}>
                    {/* No case option */}
                    {form.event_type !== 'treatment' && (
                      <div
                        onClick={clearCase}
                        style={{ padding: '8px 14px', cursor: 'pointer', fontSize: '0.82rem', color: 'var(--text-muted)', borderBottom: '1px solid rgba(255,255,255,0.06)', display: 'flex', alignItems: 'center', gap: 6 }}
                        onMouseEnter={e => e.currentTarget.style.background = 'var(--zeus-navy-light)'}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                      >
                        <X size={12} /> No matter
                      </div>
                    )}
                    {filteredCases.length === 0 ? (
                      <div style={{ padding: '12px 14px', fontSize: '0.82rem', color: 'var(--text-muted)', textAlign: 'center' }}>
                        No cases found
                      </div>
                    ) : filteredCases.slice(0, 50).map(c => {
                      const cid = c._id || c.id;
                      const isSelected = form.case_id === cid;
                      return (
                        <div
                          key={cid}
                          onClick={() => selectCase(c)}
                          style={{
                            padding: '9px 14px', cursor: 'pointer',
                            background: isSelected ? 'rgba(200,168,75,0.08)' : 'transparent',
                            borderBottom: '1px solid rgba(255,255,255,0.04)',
                            transition: 'background 0.1s',
                          }}
                          onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = 'var(--zeus-navy-light)'; }}
                          onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = 'transparent'; }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                            <span style={{ fontFamily: 'var(--font-mono)', fontSize: '0.78rem', color: 'var(--zeus-gold)', fontWeight: 700 }}>
                              {c.case_number || cid.slice(0, 8)}
                            </span>
                            <span style={{ fontSize: '0.84rem', fontWeight: 600, color: 'var(--text-primary)' }}>
                              {c.client_name || '—'}
                            </span>
                          </div>
                          <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: 2, display: 'flex', gap: 10 }}>
                            {c.claim_number && <span>Claim: {c.claim_number}</span>}
                            {c.date_of_incident && <span>DOI: {c.date_of_incident.slice(0, 10)}</span>}
                            {c.case_type && <span>{c.case_type}</span>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Treatment-only: provider + appointment type */}
              {form.event_type === 'treatment' && form.case_id && (
                <div className="form-row">
                  {/* Provider search */}
                  <div className="form-group" ref={providerRef} style={{ position: 'relative' }}>
                    <label className="form-label" style={{ color: '#0d9488' }}>Treating Provider</label>
                    {form.provider_name ? (
                      <div style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        padding: '7px 10px', borderRadius: 7,
                        border: '1px solid rgba(10,112,105,0.5)', background: 'rgba(10,112,105,0.15)',
                        fontSize: '0.84rem', color: '#0d9488', fontWeight: 600,
                      }}>
                        <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{form.provider_name}</span>
                        <button onClick={() => { setF('provider_name', ''); setF('appointment_type', ''); }}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: '0 0 0 6px', flexShrink: 0, display: 'flex' }}>
                          <X size={13} />
                        </button>
                      </div>
                    ) : (
                      <>
                        <div ref={providerInputRef} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 10px', borderRadius: 7, border: '1px solid var(--zeus-border)', background: 'var(--zeus-navy-light)' }}>
                          <Search size={12} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
                          <input
                            style={{ background: 'none', border: 'none', outline: 'none', flex: 1, fontSize: '0.84rem', color: 'var(--text-primary)', fontFamily: 'inherit' }}
                            placeholder={loadingProviders ? 'Loading providers...' : caseProviders.length === 0 ? 'No providers on file for this case' : 'Search treating providers...'}
                            value={providerSearch}
                            onChange={e => { setProviderSearch(e.target.value); openProviderDropdown(); }}
                            onFocus={openProviderDropdown}
                            disabled={loadingProviders || caseProviders.length === 0}
                          />
                        </div>
                        {providerOpen && (
                          <div style={{
                            position: 'fixed',
                            top: dropdownPos.top,
                            left: dropdownPos.left,
                            width: dropdownPos.width,
                            zIndex: 99999,
                            background: 'var(--zeus-navy)', border: '1px solid var(--zeus-border)',
                            borderRadius: 8, boxShadow: '0 8px 32px rgba(0,0,0,0.7)',
                            maxHeight: 200, overflowY: 'auto',
                          }}>
                            {filteredProviders.length === 0 ? (
                              <div style={{ padding: '10px 14px', fontSize: '0.82rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>
                                {caseProviders.length === 0
                                  ? 'No treating providers on file. Add providers in the case\'s Treatment tab.'
                                  : 'No match found'}
                              </div>
                            ) : filteredProviders.map((p, i) => (
                              <div
                                key={i}
                                onClick={() => selectProvider(p)}
                                style={{ padding: '9px 14px', cursor: 'pointer', borderBottom: '1px solid rgba(255,255,255,0.04)', transition: 'background 0.1s' }}
                                onMouseEnter={e => e.currentTarget.style.background = 'var(--zeus-navy-light)'}
                                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                              >
                                <div style={{ fontWeight: 600, fontSize: '0.84rem', color: 'var(--text-primary)' }}>{p.name}</div>
                                {p.type && <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: 2 }}>{p.type}</div>}
                              </div>
                            ))}
                          </div>
                        )}
                      </>
                    )}
                  </div>

                  {/* Appointment type from Treatment Types */}
                  <div className="form-group">
                    <label className="form-label" style={{ color: '#0d9488' }}>Appointment Type</label>
                    <select
                      className="form-select"
                      value={form.appointment_type}
                      onChange={e => {
                        setF('appointment_type', e.target.value);
                        if (!form.title && form.provider_name) {
                          setF('title', `${form.provider_name} — ${e.target.value}`);
                        }
                      }}
                      disabled={!form.provider_name}
                    >
                      <option value="">— {form.provider_name ? 'Select type' : 'Select provider first'} —</option>
                      {treatTypes.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                </div>
              )}

              {/* Title */}
              <div className="form-group">
                <label className="form-label">Event Title *</label>
                <input
                  className="form-input"
                  value={form.title}
                  onChange={e => setF('title', e.target.value)}
                  placeholder={
                    form.event_type === 'treatment' ? 'e.g. Dr. Martinez — Follow-Up' :
                    form.event_type === 'hearing'   ? 'e.g. Pre-trial Hearing — Carter' :
                    form.event_type === 'deadline'  ? 'e.g. Discovery cutoff — Sharma' :
                    form.event_type === 'mediation' ? 'e.g. Mediation Session — Reyes' :
                    'Event title'
                  }
                />
              </div>

              {/* Date + Time */}
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Date *</label>
                  <input className="form-input" type="date" value={form.date} onChange={e => setF('date', e.target.value)} />
                </div>
                <div className="form-group">
                  <label className="form-label">Time</label>
                  <input className="form-input" type="time" value={form.time} onChange={e => setF('time', e.target.value)} />
                </div>
              </div>

              {/* Assign + Location */}
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Assigned To</label>
                  <select className="form-select" value={form.assigned_to} onChange={e => setF('assigned_to', e.target.value)}>
                    <option value="">— Me —</option>
                    {users.map(u => (
                      <option key={u._id || u.id} value={u._id || u.id}>
                        {u.first_name} {u.last_name}
                      </option>
                    ))}
                  </select>
                </div>
                {form.event_type !== 'treatment' && (
                  <div className="form-group">
                    <label className="form-label">Location</label>
                    <input className="form-input" value={form.location} onChange={e => setF('location', e.target.value)} placeholder="Courthouse, office..." />
                  </div>
                )}
              </div>

              {/* Notes */}
              <div className="form-group">
                <label className="form-label">Notes</label>
                <textarea className="form-textarea" rows={2} value={form.notes} onChange={e => setF('notes', e.target.value)} placeholder="Optional notes..." />
              </div>

              {/* Treatment reminder */}
              {form.event_type === 'treatment' && form.case_id && form.provider_name && form.appointment_type && (
                <div style={{
                  padding: '10px 14px', borderRadius: 8,
                  border: '1px solid rgba(10,112,105,0.4)', background: 'rgba(10,112,105,0.1)',
                  fontSize: '0.8rem', color: '#0d9488',
                  display: 'flex', alignItems: 'center', gap: 8,
                }}>
                  <CheckCircle size={14} style={{ flexShrink: 0 }} />
                  Saving will auto-create a <strong style={{ marginLeft: 3, marginRight: 3 }}>{form.appointment_type}</strong> consultation in the case's Treatment Tab.
                </div>
              )}
            </div>

            {/* Footer */}
            <div style={{ padding: '12px 20px', borderTop: '1px solid var(--zeus-border)', display: 'flex', gap: 8, justifyContent: 'flex-end', flexShrink: 0 }}>
              <button className="btn btn-ghost" onClick={() => setShowAdd(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving…' : form.event_type === 'treatment' ? ' Save & Create Consultation' : ' Save Event'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ══ EVENT DETAIL MODAL ═══════════════════════════════════ */}
      {showDetail && detailEv && (() => {
        const cfg = TYPE_CONFIG[detailEv.event_type] || TYPE_CONFIG.deadline;
        const Icon = cfg.icon;
        const ds = eventDateStr(detailEv);
        const ts = eventTimeStr(detailEv);
        return (
          <div style={{ position: 'fixed', inset: 0, zIndex: 999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.55)' }} onClick={() => setShowDetail(false)} />
            <div style={{
              position: 'relative', width: 420, maxWidth: '96vw',
              background: 'var(--zeus-navy)', border: '1px solid var(--zeus-border)',
              borderRadius: 12, boxShadow: '0 20px 60px rgba(0,0,0,0.5)', overflow: 'hidden',
            }}>
              <div style={{ height: 4, background: cfg.color }} />
              <div style={{ padding: '16px 18px', borderBottom: '1px solid var(--zeus-border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{ padding: '4px 10px', borderRadius: 20, background: cfg.bg, color: cfg.text, fontSize: '0.75rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 5 }}>
                    <Icon size={12} /> {cfg.label}
                  </div>
                </div>
                <button onClick={() => setShowDetail(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', display: 'flex', padding: 4 }}>
                  <X size={15} />
                </button>
              </div>
              <div style={{ padding: '18px 20px' }}>
                <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.2rem', color: 'var(--text-primary)', marginBottom: 4 }}>
                  {detailEv.title}
                </div>

                {/* Claimant — shown prominently when event is linked to a case */}
                {(detailEv.case?.client_name || detailEv.case?.case_number || detailEv.case_id) && (() => {
                  const caseId = detailEv.case?.id || detailEv.case_id;
                  const canNav = !!caseId;
                  return (
                    <div
                      onClick={() => canNav && navigate(`/cases/${caseId}`, { state: { tab: 'treatment' } })}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14,
                        padding: '8px 12px', borderRadius: 8,
                        background: 'rgba(212,168,83,0.08)', border: '1px solid rgba(212,168,83,0.2)',
                        cursor: canNav ? 'pointer' : 'default',
                        transition: 'background 0.12s',
                      }}
                      onMouseEnter={e => { if (canNav) e.currentTarget.style.background = 'rgba(212,168,83,0.16)'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = 'rgba(212,168,83,0.08)'; }}
                      title={canNav ? 'Open Treatment Tab' : undefined}
                    >
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: 'var(--zeus-gold)', marginBottom: 2 }}>
                          Claimant {canNav && <span style={{ fontSize: '0.6rem', opacity: 0.7 }}>— click to open Treatment Tab</span>}
                        </div>
                        <div style={{ fontSize: '0.95rem', fontWeight: 700, color: canNav ? 'var(--zeus-gold)' : 'var(--text-primary)', textDecoration: canNav ? 'underline' : 'none' }}>
                          {detailEv.case?.client_name || '—'}
                        </div>
                        {detailEv.case?.date_of_birth && (
                          <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: 1 }}>
                            DOB: {new Date(detailEv.case.date_of_birth).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                          </div>
                        )}
                      </div>
                      {detailEv.case?.case_number && (
                        <div style={{ fontSize: '0.75rem', fontFamily: 'var(--font-mono)', color: 'var(--text-muted)', flexShrink: 0 }}>
                          {detailEv.case.case_number}
                        </div>
                      )}
                    </div>
                  );
                })()}

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div>
                    <div style={{ fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: 'var(--text-muted)', marginBottom: 3 }}>Date</div>
                    <div style={{ fontSize: '0.88rem', color: 'var(--text-primary)', fontWeight: 500 }}>{fmtShortDate(ds)}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: 'var(--text-muted)', marginBottom: 3 }}>Time</div>
                    <div style={{ fontSize: '0.88rem', color: 'var(--text-primary)', fontWeight: 500 }}>{ts ? fmtTime(ts) : 'All day'}</div>
                  </div>
                  {detailEv.provider_name && (
                    <div>
                      <div style={{ fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: 'var(--text-muted)', marginBottom: 3 }}>Provider</div>
                      <div style={{ fontSize: '0.85rem', color: '#0d9488', fontWeight: 600 }}>{detailEv.provider_name}</div>
                    </div>
                  )}
                  {detailEv.appointment_type && (
                    <div>
                      <div style={{ fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: 'var(--text-muted)', marginBottom: 3 }}>Appointment Type</div>
                      <div style={{ fontSize: '0.85rem', color: 'var(--text-primary)' }}>{detailEv.appointment_type}</div>
                    </div>
                  )}
                  {detailEv.location && (
                    <div style={{ gridColumn: '1/-1' }}>
                      <div style={{ fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: 'var(--text-muted)', marginBottom: 3 }}>Location</div>
                      <div style={{ fontSize: '0.85rem', color: 'var(--text-primary)' }}>{detailEv.location}</div>
                    </div>
                  )}
                  {detailEv.description && (
                    <div style={{ gridColumn: '1/-1' }}>
                      <div style={{ fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: 'var(--text-muted)', marginBottom: 3 }}>Notes</div>
                      <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>{detailEv.description}</div>
                    </div>
                  )}
                </div>
                {detailEv.event_type === 'treatment' && detailEv.provider_name && (
                  <div style={{ marginTop: 14, padding: '8px 12px', borderRadius: 7, background: 'rgba(10,112,105,0.12)', border: '1px solid rgba(10,112,105,0.3)', fontSize: '0.78rem', color: '#0d9488', display: 'flex', alignItems: 'center', gap: 7 }}>
                    <CheckCircle size={13} style={{ flexShrink: 0 }} /> Consultation auto-created in case Treatment Tab
                  </div>
                )}
                <div style={{ display: 'flex', gap: 8, marginTop: 16, paddingTop: 14, borderTop: '1px solid var(--zeus-border)' }}>
                  <button
                    className="btn btn-ghost btn-sm"
                    style={{ flex: 1, justifyContent: 'center', color: '#ef4444', border: '1px solid rgba(239,68,68,0.2)' }}
                    onClick={() => handleDelete(detailEv)}
                  >
                    Delete
                  </button>
                  <button className="btn btn-ghost btn-sm" style={{ flex: 1, justifyContent: 'center' }} onClick={() => setShowDetail(false)}>Close</button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}
      <ConfirmModal
        isOpen={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        onConfirm={doDelete}
        title="Delete Event"
        message="This will permanently remove the event from the calendar."
        detail={confirmDelete && (
          <div>
            <div style={{ fontWeight: 700 }}>{confirmDelete.title}</div>
            <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: 3 }}>
              {confirmDelete.start_time ? new Date(confirmDelete.start_time).toLocaleDateString('en-US', { weekday:'short', month:'short', day:'numeric' }) : ''}
              {confirmDelete.event_type === 'treatment' && ' · Note: the consultation in the Treatment tab will remain.'}
            </div>
          </div>
        )}
        confirmLabel="Delete Event"
      />
    </>
  );
}
